import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Alert, AlertDescription } from '../../components/ui/Alert';
import { Badge } from '../../components/ui/Badge';
import { 
  Shield, 
  Save, 
  RefreshCw, 
  CheckCircle, 
  AlertCircle,
  Lock,
  Smartphone,
  Clock,
  Eye,
  Activity,
  Database,
  AlertTriangle
} from 'lucide-react';
import MockDataService from '../../services/MockDataService';

const SecuritySettings = () => {
  const [activeTab, setActiveTab] = useState('password');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  
  // State for different security sections
  const [securitySettings, setSecuritySettings] = useState({});
  const [passwordPolicy, setPasswordPolicy] = useState({});
  const [twoFactorSettings, setTwoFactorSettings] = useState({});
  const [sessionSettings, setSessionSettings] = useState({});

  useEffect(() => {
    loadSecuritySettings();
  }, []);

  const loadSecuritySettings = async () => {
    setLoading(true);
    try {
      const [securityRes, passwordRes, twoFactorRes, sessionRes] = await Promise.all([
        MockDataService.getSecuritySettings(),
        MockDataService.getPasswordPolicy(),
        MockDataService.getTwoFactorAuthSettings(),
        MockDataService.getSessionSettings()
      ]);

      setSecuritySettings(securityRes.data);
      setPasswordPolicy(passwordRes.data);
      setTwoFactorSettings(twoFactorRes.data);
      setSessionSettings(sessionRes.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to load security settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Simulate saving security settings
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update localStorage for persistence
      localStorage.setItem('securitySettings', JSON.stringify(securitySettings));
      localStorage.setItem('passwordPolicy', JSON.stringify(passwordPolicy));
      localStorage.setItem('twoFactorSettings', JSON.stringify(twoFactorSettings));
      localStorage.setItem('sessionSettings', JSON.stringify(sessionSettings));
      
      setMessage({ type: 'success', text: 'Security settings saved successfully' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save security settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading security settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Security Settings</h1>
          <p className="text-gray-600">Configure security policies and authentication settings</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadSecuritySettings}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Save Settings
          </Button>
        </div>
      </div>

      {message.text && (
        <Alert className={message.type === 'error' ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'}>
          {message.type === 'error' ? (
            <AlertCircle className="h-4 w-4 text-red-600" />
          ) : (
            <CheckCircle className="h-4 w-4 text-green-600" />
          )}
          <AlertDescription className={message.type === 'error' ? 'text-red-800' : 'text-green-800'}>
            {message.text}
          </AlertDescription>
        </Alert>
      )}

      <div className="space-y-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('password')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'password'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Lock className="h-4 w-4 inline mr-2" />
              Password Policy
            </button>
            <button
              onClick={() => setActiveTab('twofactor')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'twofactor'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Smartphone className="h-4 w-4 inline mr-2" />
              Two-Factor Auth
            </button>
            <button
              onClick={() => setActiveTab('session')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'session'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Clock className="h-4 w-4 inline mr-2" />
              Session Management
            </button>
            <button
              onClick={() => setActiveTab('monitoring')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'monitoring'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Activity className="h-4 w-4 inline mr-2" />
              Security Monitoring
            </button>
          </nav>
        </div>

        {activeTab === 'password' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Password Policy Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Password Requirements</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="minLength">Minimum Length</Label>
                    <Input
                      id="minLength"
                      type="number"
                      value={passwordPolicy.requirements?.find(r => r.name === 'Minimum Length')?.value || 8}
                      onChange={(e) => {
                        const newRequirements = passwordPolicy.requirements?.map(req => 
                          req.name === 'Minimum Length' ? { ...req, value: parseInt(e.target.value) } : req
                        );
                        setPasswordPolicy({ ...passwordPolicy, requirements: newRequirements });
                      }}
                      min="6"
                      max="128"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="maxLength">Maximum Length</Label>
                    <Input
                      id="maxLength"
                      type="number"
                      value={passwordPolicy.requirements?.find(r => r.name === 'Maximum Length')?.value || 128}
                      onChange={(e) => {
                        const newRequirements = passwordPolicy.requirements?.map(req => 
                          req.name === 'Maximum Length' ? { ...req, value: parseInt(e.target.value) } : req
                        );
                        setPasswordPolicy({ ...passwordPolicy, requirements: newRequirements });
                      }}
                      min="8"
                      max="256"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <h4 className="font-semibold">Character Requirements</h4>
                  <div className="grid grid-cols-2 gap-4">
                    {passwordPolicy.requirements?.slice(2).map((requirement, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <Label htmlFor={`req-${index}`}>{requirement.name}</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            id={`req-${index}`}
                            type="number"
                            value={requirement.value}
                            onChange={(e) => {
                              const newRequirements = passwordPolicy.requirements?.map((req, i) => 
                                i === index + 2 ? { ...req, value: parseInt(e.target.value) } : req
                              );
                              setPasswordPolicy({ ...passwordPolicy, requirements: newRequirements });
                            }}
                            className="w-16"
                            min="0"
                            max="10"
                          />
                  <input
                    type="checkbox"
                    checked={requirement.enabled}
                    onChange={(e) => {
                      const newRequirements = passwordPolicy.requirements?.map((req, i) => 
                        i === index + 2 ? { ...req, enabled: e.target.checked } : req
                      );
                      setPasswordPolicy({ ...passwordPolicy, requirements: newRequirements });
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <div className="border-t border-gray-200 my-6"></div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Password Restrictions</h3>
                <div className="space-y-3">
                  {passwordPolicy.restrictions?.map((restriction, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label>{restriction.name}</Label>
                        <p className="text-sm text-gray-600">
                          {restriction.name === 'Prevent Common Passwords' && 'Block passwords from common password lists'}
                          {restriction.name === 'Prevent User Information' && 'Block passwords containing user information'}
                          {restriction.name === 'Prevent Dictionary Words' && 'Block common dictionary words'}
                          {restriction.name === 'Prevent Sequential Characters' && 'Block sequential character patterns'}
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={restriction.enabled}
                        onChange={(e) => {
                          const newRestrictions = passwordPolicy.restrictions?.map((rest, i) => 
                            i === index ? { ...rest, enabled: e.target.checked } : rest
                          );
                          setPasswordPolicy({ ...passwordPolicy, restrictions: newRestrictions });
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="border-t border-gray-200 my-6"></div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Password Expiration</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="expirationDays">Expiration Days</Label>
                    <Input
                      id="expirationDays"
                      type="number"
                      value={passwordPolicy.expiration?.days || 90}
                      onChange={(e) => setPasswordPolicy({
                        ...passwordPolicy,
                        expiration: { ...passwordPolicy.expiration, days: parseInt(e.target.value) }
                      })}
                      min="30"
                      max="365"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="warningDays">Warning Days</Label>
                    <Input
                      id="warningDays"
                      type="number"
                      value={passwordPolicy.expiration?.warningDays || 7}
                      onChange={(e) => setPasswordPolicy({
                        ...passwordPolicy,
                        expiration: { ...passwordPolicy.expiration, warningDays: parseInt(e.target.value) }
                      })}
                      min="1"
                      max="30"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gracePeriodDays">Grace Period Days</Label>
                    <Input
                      id="gracePeriodDays"
                      type="number"
                      value={passwordPolicy.expiration?.gracePeriodDays || 3}
                      onChange={(e) => setPasswordPolicy({
                        ...passwordPolicy,
                        expiration: { ...passwordPolicy.expiration, gracePeriodDays: parseInt(e.target.value) }
                      })}
                      min="0"
                      max="7"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="enableExpiration">Enable Password Expiration</Label>
                    <p className="text-sm text-gray-600">Require users to change passwords periodically</p>
                  </div>
                  <input
                    type="checkbox"
                    id="enableExpiration"
                    checked={passwordPolicy.expiration?.enabled || false}
                    onChange={(e) => setPasswordPolicy({
                      ...passwordPolicy,
                      expiration: { ...passwordPolicy.expiration, enabled: e.target.checked }
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {activeTab === 'twofactor' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5" />
                Two-Factor Authentication
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="enable2FA">Enable Two-Factor Authentication</Label>
                  <p className="text-sm text-gray-600">Require additional verification for user accounts</p>
                </div>
                <input
                  type="checkbox"
                  id="enable2FA"
                  checked={twoFactorSettings.enabled || false}
                  onChange={(e) => setTwoFactorSettings({ ...twoFactorSettings, enabled: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
              </div>
              
              <div className="border-t border-gray-200 my-6"></div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Available Methods</h3>
                <div className="space-y-3">
                  {twoFactorSettings.methods?.map((method, index) => (
                    <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="space-y-1">
                        <Label className="font-semibold">{method.name}</Label>
                        <p className="text-sm text-gray-600">{method.description}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={method.enabled}
                        onChange={(e) => {
                          const newMethods = twoFactorSettings.methods?.map((m, i) => 
                            i === index ? { ...m, enabled: e.target.checked } : m
                          );
                          setTwoFactorSettings({ ...twoFactorSettings, methods: newMethods });
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="border-t border-gray-200 my-6"></div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Role Requirements</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Require for Administrators</Label>
                      <p className="text-sm text-gray-600">Force 2FA for all admin users</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={twoFactorSettings.requirements?.requireForAdmin || false}
                      onChange={(e) => setTwoFactorSettings({
                        ...twoFactorSettings,
                        requirements: { ...twoFactorSettings.requirements, requireForAdmin: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Require for Agents</Label>
                      <p className="text-sm text-gray-600">Force 2FA for all agent users</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={twoFactorSettings.requirements?.requireForAgents || false}
                      onChange={(e) => setTwoFactorSettings({
                        ...twoFactorSettings,
                        requirements: { ...twoFactorSettings.requirements, requireForAgents: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Require for Users</Label>
                      <p className="text-sm text-gray-600">Force 2FA for all portal users</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={twoFactorSettings.requirements?.requireForUsers || false}
                      onChange={(e) => setTwoFactorSettings({
                        ...twoFactorSettings,
                        requirements: { ...twoFactorSettings.requirements, requireForUsers: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>
              
              <div className="border-t border-gray-200 my-6"></div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">2FA Statistics</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Total Users</Label>
                    <span className="text-2xl font-bold">{twoFactorSettings.statistics?.totalUsers || 0}</span>
                  </div>
                  <div className="space-y-2">
                    <Label>2FA Enabled</Label>
                    <span className="text-2xl font-bold text-green-600">{twoFactorSettings.statistics?.enabledUsers || 0}</span>
                  </div>
                  <div className="space-y-2">
                    <Label>Enablement Rate</Label>
                    <span className="text-2xl font-bold">{twoFactorSettings.statistics?.enabledPercentage || 0}%</span>
                  </div>
                  <div className="space-y-2">
                    <Label>Backup Codes</Label>
                    <span className="text-2xl font-bold">{twoFactorSettings.settings?.backupCodes || 0}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {activeTab === 'session' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Session Management
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Session Timeout</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sessionTimeout">Session Timeout (minutes)</Label>
                    <Input
                      id="sessionTimeout"
                      type="number"
                      value={sessionSettings.timeout?.minutes || 30}
                      onChange={(e) => setSessionSettings({
                        ...sessionSettings,
                        timeout: { ...sessionSettings.timeout, minutes: parseInt(e.target.value) }
                      })}
                      min="5"
                      max="480"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="warningMinutes">Warning Minutes</Label>
                    <Input
                      id="warningMinutes"
                      type="number"
                      value={sessionSettings.timeout?.warningMinutes || 5}
                      onChange={(e) => setSessionSettings({
                        ...sessionSettings,
                        timeout: { ...sessionSettings.timeout, warningMinutes: parseInt(e.target.value) }
                      })}
                      min="1"
                      max="30"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="enableTimeout">Enable Session Timeout</Label>
                    <p className="text-sm text-gray-600">Automatically log out users after inactivity</p>
                  </div>
                  <input
                    type="checkbox"
                    id="enableTimeout"
                    checked={sessionSettings.timeout?.enabled || false}
                    onChange={(e) => setSessionSettings({
                      ...sessionSettings,
                      timeout: { ...sessionSettings.timeout, enabled: e.target.checked }
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </div>
              
              <div className="border-t border-gray-200 my-6"></div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Concurrent Sessions</h3>
                <div className="space-y-2">
                  <Label htmlFor="maxSessions">Maximum Concurrent Sessions</Label>
                  <Input
                    id="maxSessions"
                    type="number"
                    value={sessionSettings.concurrent?.maxSessions || 3}
                    onChange={(e) => setSessionSettings({
                      ...sessionSettings,
                      concurrent: { ...sessionSettings.concurrent, maxSessions: parseInt(e.target.value) }
                    })}
                    min="1"
                    max="10"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="enableConcurrent">Enable Concurrent Session Limit</Label>
                    <p className="text-sm text-gray-600">Limit number of simultaneous sessions per user</p>
                  </div>
                  <input
                    type="checkbox"
                    id="enableConcurrent"
                    checked={sessionSettings.concurrent?.enabled || false}
                    onChange={(e) => setSessionSettings({
                      ...sessionSettings,
                      concurrent: { ...sessionSettings.concurrent, enabled: e.target.checked }
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </div>
              
              <div className="border-t border-gray-200 my-6"></div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Remember Me</h3>
                <div className="space-y-2">
                  <Label htmlFor="rememberMeDays">Remember Me Duration (days)</Label>
                  <Input
                    id="rememberMeDays"
                    type="number"
                    value={sessionSettings.rememberMe?.days || 30}
                    onChange={(e) => setSessionSettings({
                      ...sessionSettings,
                      rememberMe: { ...sessionSettings.rememberMe, days: parseInt(e.target.value) }
                    })}
                    min="1"
                    max="365"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label htmlFor="enableRememberMe">Enable Remember Me</Label>
                    <p className="text-sm text-gray-600">Allow users to stay logged in longer</p>
                  </div>
                  <input
                    type="checkbox"
                    id="enableRememberMe"
                    checked={sessionSettings.rememberMe?.enabled || false}
                    onChange={(e) => setSessionSettings({
                      ...sessionSettings,
                      rememberMe: { ...sessionSettings.rememberMe, enabled: e.target.checked }
                    })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                </div>
              </div>
              
              <div className="border-t border-gray-200 my-6"></div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Session Statistics</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Active Sessions</Label>
                    <span className="text-2xl font-bold">{sessionSettings.statistics?.activeSessions || 0}</span>
                  </div>
                  <div className="space-y-2">
                    <Label>Average Duration</Label>
                    <span className="text-2xl font-bold">{sessionSettings.statistics?.averageSessionDuration || '0 hours'}</span>
                  </div>
                  <div className="space-y-2">
                    <Label>Sessions Today</Label>
                    <span className="text-2xl font-bold">{sessionSettings.statistics?.sessionsToday || 0}</span>
                  </div>
                  <div className="space-y-2">
                    <Label>Expired Sessions</Label>
                    <span className="text-2xl font-bold">{sessionSettings.statistics?.expiredSessions || 0}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        )}

        {activeTab === 'monitoring' && (
          <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" />
                Security Monitoring
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Audit Logging</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Enable Audit Logging</Label>
                      <p className="text-sm text-gray-600">Log all system activities and user actions</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={securitySettings.securityMonitoring?.enableAuditLog || false}
                      onChange={(e) => setSecuritySettings({
                        ...securitySettings,
                        securityMonitoring: { ...securitySettings.securityMonitoring, enableAuditLog: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Enable Login Logging</Label>
                      <p className="text-sm text-gray-600">Log all login attempts and sessions</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={securitySettings.securityMonitoring?.enableLoginLogging || false}
                      onChange={(e) => setSecuritySettings({
                        ...securitySettings,
                        securityMonitoring: { ...securitySettings.securityMonitoring, enableLoginLogging: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
              </div>
              
              <div className="border-t border-gray-200 my-6"></div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Security Alerts</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Failed Login Alerts</Label>
                      <p className="text-sm text-gray-600">Alert on multiple failed login attempts</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={securitySettings.securityMonitoring?.enableFailedLoginAlerts || false}
                      onChange={(e) => setSecuritySettings({
                        ...securitySettings,
                        securityMonitoring: { ...securitySettings.securityMonitoring, enableFailedLoginAlerts: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Suspicious Activity Alerts</Label>
                      <p className="text-sm text-gray-600">Alert on unusual user behavior patterns</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={securitySettings.securityMonitoring?.enableSuspiciousActivityAlerts || false}
                      onChange={(e) => setSecuritySettings({
                        ...securitySettings,
                        securityMonitoring: { ...securitySettings.securityMonitoring, enableSuspiciousActivityAlerts: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="alertThreshold">Alert Threshold</Label>
                  <Input
                    id="alertThreshold"
                    type="number"
                    value={securitySettings.securityMonitoring?.alertThreshold || 5}
                    onChange={(e) => setSecuritySettings({
                      ...securitySettings,
                      securityMonitoring: { ...securitySettings.securityMonitoring, alertThreshold: parseInt(e.target.value) }
                    })}
                    min="1"
                    max="20"
                  />
                </div>
              </div>
              
              <div className="border-t border-gray-200 my-6"></div>
              
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Data Protection</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Enable Data Encryption</Label>
                      <p className="text-sm text-gray-600">Encrypt sensitive data at rest</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={securitySettings.dataProtection?.enableDataEncryption || false}
                      onChange={(e) => setSecuritySettings({
                        ...securitySettings,
                        dataProtection: { ...securitySettings.dataProtection, enableDataEncryption: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Enable Data Masking</Label>
                      <p className="text-sm text-gray-600">Mask sensitive data in logs and displays</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={securitySettings.dataProtection?.enableDataMasking || false}
                      onChange={(e) => setSecuritySettings({
                        ...securitySettings,
                        dataProtection: { ...securitySettings.dataProtection, enableDataMasking: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label>Enable Data Retention</Label>
                      <p className="text-sm text-gray-600">Automatically delete old data</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={securitySettings.dataProtection?.enableDataRetention || false}
                      onChange={(e) => setSecuritySettings({
                        ...securitySettings,
                        dataProtection: { ...securitySettings.dataProtection, enableDataRetention: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="retentionDays">Data Retention Period (days)</Label>
                  <Input
                    id="retentionDays"
                    type="number"
                    value={securitySettings.dataProtection?.retentionPeriodDays || 2555}
                    onChange={(e) => setSecuritySettings({
                      ...securitySettings,
                      dataProtection: { ...securitySettings.dataProtection, retentionPeriodDays: parseInt(e.target.value) }
                    })}
                    min="30"
                    max="3650"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecuritySettings;
